import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import {
  eventWindowHours,
  GUTTER_WIDTH,
  HOURS_PER_DAY,
  LANE_WIDTH,
  LAYER,
  overlapsEventWindow,
  PX_PER_HOUR,
  shiftColor,
  TShiftScheduleEvent,
  TZ,
} from "../constants";
import { groupShiftsByCategory } from "../utils";

import { HourGrid, ShiftsColumn } from "./ShiftsColumn";
import { ShiftTimeLineColumn } from "./ShiftTimeLineColumn";

interface CalendarProps {
  daysToShow: string[];
  calendarShifts: TShiftScheduleEvent[];
  setSelectedShift: (shiftId: number) => void;
  /** rendered at the right end of the day jump row */
  dayBarActions?: React.ReactNode;
  /** pack every shift into one column; overlaps widen lanes as usual */
  collapseToSingleColumn?: boolean;
}

const DAY_HEIGHT = HOURS_PER_DAY * PX_PER_HOUR;

/**
 * The sticky header covers the top of the scroll viewport, and the body starts
 * directly below it, so those two offsets cancel: scrolling to exactly
 * dayIndex * DAY_HEIGHT lands that day's midnight rule just under the header.
 */
const scrollTopForDay = (dayIndex: number) => dayIndex * DAY_HEIGHT;

/** Hours elapsed since the start of the first day shown, ticking each minute. */
const useHoursSince = (eventStart: string) => {
  const measure = () =>
    moment().tz(TZ).diff(moment.tz(eventStart, TZ), "hours", true);
  const [hours, setHours] = useState(measure);

  useEffect(() => {
    const tick = () =>
      setHours(moment().tz(TZ).diff(moment.tz(eventStart, TZ), "hours", true));
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [eventStart]);

  return hours;
};

export const Calendar: React.FC<CalendarProps> = ({
  daysToShow,
  calendarShifts,
  setSelectedShift,
  dayBarActions,
  collapseToSingleColumn = false,
}) => {
  const totalHours = eventWindowHours(daysToShow.length);

  // shifts outside the event window would otherwise be positioned thousands of
  // hours off-canvas and still widen every section
  const inRange = useMemo(
    () =>
      calendarShifts.filter((shift) =>
        overlapsEventWindow(
          shift.blocksFromTop,
          shift.numberOfBlocks,
          daysToShow.length
        )
      ),
    [calendarShifts, daysToShow.length]
  );

  const sections = useMemo(
    () =>
      groupShiftsByCategory(inRange, {
        collapseToSingleColumn,
        singleColumnLabel: "My shifts",
      }),
    [inRange, collapseToSingleColumn]
  );

  const hoursNow = useHoursSince(daysToShow[0]);
  const showNowLine = hoursNow >= 0 && hoursNow <= totalHours;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      // lock tooltips are portaled to the body, so they would hang in place
      // while the sheet scrolls underneath them
      ReactTooltip.hide();
      setActiveDay(
        Math.min(Math.floor(el.scrollTop / DAY_HEIGHT), daysToShow.length - 1)
      );
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [daysToShow.length]);

  const jumpToDay = useCallback((dayIndex: number) => {
    scrollRef.current?.scrollTo({
      top: scrollTopForDay(dayIndex),
      behavior: "smooth",
    });
  }, []);

  return (
    <Container>
      {(daysToShow.length > 1 || dayBarActions) && (
        <DayJumpBar>
          <DayChips>
            {daysToShow.length > 1 &&
              daysToShow.map((day, i) => (
                <DayChip
                  key={day}
                  type="button"
                  active={i === activeDay}
                  aria-current={i === activeDay}
                  onClick={() => jumpToDay(i)}
                >
                  {moment(day).format("ddd")}
                </DayChip>
              ))}
          </DayChips>
          {dayBarActions}
        </DayJumpBar>
      )}
      <SubContainer ref={scrollRef}>
        <ScrollContainer>
          <HeaderRow>
            <Corner />
            {sections.map((section) => (
              <SectionHeader
                key={section.category}
                accent={shiftColor(section.category)}
                style={{ width: section.lanes * LANE_WIDTH }}
              >
                <span>{section.category}</span>
              </SectionHeader>
            ))}
            {/* pads the header out when the sections do not fill the width */}
            <Filler />
          </HeaderRow>

          <BodyRow>
            {showNowLine && (
              <NowLine
                style={{ top: hoursNow * PX_PER_HOUR, left: GUTTER_WIDTH }}
              />
            )}

            <ShiftTimeLineColumn
              daysToShow={daysToShow}
              totalHours={totalHours}
            />

            {sections.map((section) => (
              <ShiftsColumn
                key={section.category}
                section={section}
                totalHours={totalHours}
                setSelectedShift={setSelectedShift}
              />
            ))}

            {/* keeps the hour rules running to the right edge when the
                sections are narrower than the viewport */}
            <Filler>
              <HourGrid totalHours={totalHours} />
            </Filler>
          </BodyRow>
        </ScrollContainer>
      </SubContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  margin-bottom: 16px;
  height: 100%;
  /* trap sticky header / tile z-index so they cannot paint over the nav */
  position: relative;
  z-index: 0;
  isolation: isolate;
`;

const DayJumpBar = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 20px;
`;

const DayChips = styled.div`
  display: flex;
  gap: 6px;
`;

/** matches the category filter chips on the schedule pages above it */
const DayChip = styled.button<{ active: boolean }>`
  border: none;
  border-radius: 27.568px;
  padding: 4px 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${({ theme, active }) =>
    active
      ? theme.globalConstants.color.brandPrimary
      : theme.globalConstants.color.backgroundGrayDark};
  color: ${({ theme, active }) =>
    active
      ? theme.globalConstants.color.white
      : theme.globalConstants.color.textSecondary};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: 1px;
  }
`;

const SubContainer = styled.div`
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 7px;
`;

const ScrollContainer = styled.div`
  width: max-content;
  min-width: 100%;
  position: relative;
`;

const HeaderRow = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: ${LAYER.header};
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 0 ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const Corner = styled.div`
  flex: 0 0 ${GUTTER_WIDTH}px;
  position: sticky;
  left: 0;
  z-index: ${LAYER.corner};
  background: ${({ theme }) => theme.globalConstants.color.white};
  border-right: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const SectionHeader = styled.div<{ accent: string }>`
  flex: none;
  box-sizing: border-box;
  padding: 12px 10px;
  border-left: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  overflow: hidden;

  span {
    display: block;
    border-left: 4px solid ${({ accent }) => accent};
    padding-left: 8px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    color: ${({ theme }) => theme.globalConstants.color.textBody};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const BodyRow = styled.div`
  display: flex;
  position: relative;
`;

/** absorbs any width the category sections leave over, so the hour rules and
    the header background still reach the right edge */
const Filler = styled.div`
  flex: 1 0 0;
  min-width: 0;
`;

const NowLine = styled.div`
  position: absolute;
  right: 0;
  height: 0;
  z-index: ${LAYER.nowLine};
  border-bottom: 2px solid
    ${({ theme }) => theme.globalConstants.color.backgroundDanger};

  &::before {
    content: "";
    position: absolute;
    top: -4px;
    left: -4px;
    height: 8px;
    width: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.globalConstants.color.backgroundDanger};
  }
`;
